// Lightweight authz helpers for frontend group-based gating.
// - Works with Inertia shared `auth.groups: GroupKey[]`
// - Pure functions (no hooks) for easy reuse and testing
import type { GroupKey } from "@/constants/groups";
import type { PurchaseRequestApprovalState } from "@/types";

export function hasGroup(groups: string[], key: GroupKey): boolean {
  return groups.includes(key);
}

export function anyGroup(groups: string[], keys: GroupKey[] = []): boolean {
  if (!keys.length) return true;
  return keys.some((k) => groups.includes(k));
}

export function allGroups(groups: string[], keys: GroupKey[] = []): boolean {
  if (!keys.length) return true;
  return keys.every((k) => groups.includes(k));
}

export function noneGroup(groups: string[], keys: GroupKey[] = []): boolean {
  if (!keys.length) return true;
  return keys.every((k) => !groups.includes(k));
}

export function isAllowed(
  groups: string[],
  opts: { anyOf?: GroupKey[]; allOf?: GroupKey[]; noneOf?: GroupKey[] } = {},
): boolean {
  const { anyOf, allOf, noneOf } = opts;
  const anyOk = anyGroup(groups, anyOf);
  const allOk = allGroups(groups, allOf);
  const noneOk = noneGroup(groups, noneOf);
  return anyOk && allOk && noneOk;
}

/**
 * Check if user can see approval-related components based on groups and approval flow participation
 * @param userGroups - User's groups from auth.groups
 * @param approval - Purchase request approval state
 * @param currentUserId - Current user ID from auth.user.id
 * @param allowedGroups - Groups that can always see the component (e.g., ['buyer', 'supervisor'])
 * @returns boolean - Whether user can see the component
 */
export function canSeeApprovalComponent(
  userGroups: string[],
  approval: PurchaseRequestApprovalState,
  currentUserId: number,
  allowedGroups: GroupKey[] = [],
): boolean {
  // If user belongs to any of the allowed groups, they can see the component
  if (anyGroup(userGroups, allowedGroups)) {
    return true;
  }

  // If user is the assigned requester, they can see it
  if (approval.requester_user_id && approval.requester_user_id === currentUserId) {
    return true;
  }

  // If approval flow was never started and user is not in allowed groups or requester,
  // they cannot see it
  if (!approval.started && !approval.snapshot) {
    return false;
  }

  // Check if user is/was assigned to any level in the approval flow
  // This allows visibility even after approval is completed (approved/rejected)
  const snapshotLevels = approval.snapshot?.levels ?? [];
  const isUserAssignedToLevel = snapshotLevels.some((level) =>
    (level.assignees ?? []).some((assignee) => Number(assignee.user_id ?? 0) === currentUserId)
  );

  return isUserAssignedToLevel;
}
